import { Injectable, inject } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { GraphQLClientService } from '../../../../../../core/services/graphql-client.service';
import { ViewerIdentityService } from '../../../../../../core/services/viewer-identity.service';
import { isErr, ok } from '../../../../../../core/models/api-result';
import type { ApiResult } from '../../../../../../core/models/api-result';
import { Action, matchesActionHierarchical } from '../../../../../../core/constants/action-catalogue';
import type { SearchApiSpace, SearchEntityResult } from '../../../../../../core/models/search-api.model';
import type { CmdkAvailability, CmdkItem, CmdkSpaceItem } from '../../models/cmdk.model';
import type { CmdkProviderContext } from '../../models/cmdk-provider';
import { CmdkRemoteProviderBase } from './remote.provider.base';

/**
 * RESPONSIBILITY: the "Spaces" scope — rooms, desks, parking, lockers.
 *
 * BACKEND CONTRACT: two documents, chosen by whether a building is focused.
 *
 *   searchMap(locationId: ID!, …)  when the user is on the map and a building
 *                                  is selected. Richest result: capacity,
 *                                  amenities, floor, and availability.
 *   searchEntities(…)              everywhere else (home, schedule, profile).
 *                                  `locationId` is nullable there, so this
 *                                  searches the whole estate — but returns no
 *                                  capacity, no amenities and no availability.
 *
 * The second path's poverty is surfaced, not hidden: those rows carry
 * `availability: 'unknown'`, which is exactly the case that enum member
 * exists for. Pretending a space is free because we did not ask is worse than
 * saying we do not know.
 *
 * AI CONTEXT — three things that will bite anyone editing this
 *
 * **`lexicalOnly: true` keeps the AI off the typing path.** Without it a
 * query with structure ("meeting room for 6") made `searchMap` wait ~1.9 s
 * for a Vertex embedding and, on SEARCH_AI tenants, a Claude intent
 * extraction, on every pause in typing. The API's keyword arm ranks names
 * forgivingly on its own (typos, word order, accents; rdx-platform-api
 * `space-lexical.ts`). `minQueryLength` stays 3 and the debounce in
 * `CmdkSearchService` is not optional either.
 *
 * **Other buildings too.** With a building focused, a search that finds
 * fewer than three spaces there also asks the estate (`searchEntities`), and
 * those rows say which building they are in. Before, a room in another
 * building simply did not exist as far as the palette was concerned.
 *
 * **`types: ["space"]` is a literal in the document, never interpolated.** It
 * also keeps this provider from ever fanning out to employees, which is the
 * people provider's job — `searchMap` will happily return both.
 *
 * **`includeSummary: false` is load-bearing.** Left on, the resolver invokes
 * the AI summariser for SEARCH_AI tenants on every keystroke.
 *
 * Do NOT route this through `SearchService`. That service is owned by the
 * map's search panel and holds its results in shared signals; the palette
 * writing into them would make the two surfaces overwrite each other.
 */

/** Focused-building path. Richer, and scoped to one location. */
const CMDK_SPACES_IN_LOCATION_QUERY = `
  query cmdkSpacesInLocation($locationId: ID!, $query: String!, $limit: Int) {
    searchMap(
      locationId: $locationId
      query: $query
      types: ["space"]
      limit: $limit
      includeSummary: false
      lexicalOnly: true
    ) {
      spaces {
        id
        name
        spaceType
        capacity
        email
        floorName
        score
        availableAtRequestedTime
        amenities { id name }
      }
    }
  }
`;

/**
 * Estate-wide path. `locationName` and `floorName` are already on the TS
 * interface but were missing from the map surface's own selection set — they
 * are selected here because without them a row found from /home says only a
 * name, with no way to tell two "Boardroom"s apart.
 */
const CMDK_SPACES_ANYWHERE_QUERY = `
  query cmdkSpacesAnywhere($query: String!, $limit: Int) {
    searchEntities(query: $query, types: ["space"], limit: $limit) {
      id
      name
      type
      subtype
      locationName
      floorName
      score
    }
  }
`;

const LIMIT = 8;

/** Fewer than this many in the focused building, and the estate is asked too. */
const ESTATE_FALLBACK_BELOW = 3;

@Injectable({ providedIn: 'root' })
export class CmdkSpaceProvider extends CmdkRemoteProviderBase {
  private readonly graphql = inject(GraphQLClientService);
  private readonly transloco = inject(TranslocoService);
  private readonly identity = inject(ViewerIdentityService);

  readonly id = 'space';
  readonly kind = 'space' as const;
  readonly scope = 'spaces' as const;
  /** See the header: 3, because every call is an embedding. */
  readonly minQueryLength = 3;

  /**
   * A UX gate only — `searchMap`/`searchEntities` carry `@Auth()` but no
   * action guard — so the palette does not offer a scope whose rows the
   * viewer cannot open.
   *
   * Read from the context being searched (the viewer's actions), else the
   * identity service. Until 9 Oct 2026 it read a field that only `fetch()`
   * ever set, and `fetch()` only runs once this says yes: the gate could never
   * open, so the Spaces group had not appeared in the palette since 5 Aug
   * (#278). The unit spec called `load()` directly and never asked.
   */
  isEnabled(ctx?: CmdkProviderContext): boolean {
    return matchesActionHierarchical(ctx?.userActions ?? this.identity.actions(), Action.SPACE_VIEW);
  }

  protected async fetch(ctx: CmdkProviderContext): Promise<ApiResult<CmdkItem[]>> {
    if (!ctx.locationId) return this.fetchAnywhere(ctx);

    const here = await this.fetchInLocation(ctx, ctx.locationId);
    if (isErr(here) || here.data.length >= ESTATE_FALLBACK_BELOW || ctx.signal.aborted) return here;

    // Few or none in this building: say where else it is. A failed estate
    // read costs the extra rows, never the building's own.
    const elsewhere = await this.fetchAnywhere(ctx);
    if (isErr(elsewhere)) return here;
    const seen = new Set(here.data.map(item => item.id));
    const more = elsewhere.data.filter(item => !seen.has(item.id));
    return ok([...here.data, ...more].slice(0, LIMIT));
  }

  private async fetchInLocation(
    ctx: CmdkProviderContext,
    locationId: string,
  ): Promise<ApiResult<CmdkItem[]>> {
    const result = await this.graphql.queryResult<{ spaces?: SearchApiSpace[] }>(
      CMDK_SPACES_IN_LOCATION_QUERY,
      { locationId, query: ctx.query, limit: LIMIT },
    );
    if (isErr(result)) return result;
    const spaces = result.data?.spaces ?? [];
    return ok(spaces.map(space => this.toItemFromMap(space, locationId)));
  }

  private async fetchAnywhere(ctx: CmdkProviderContext): Promise<ApiResult<CmdkItem[]>> {
    const result = await this.graphql.queryResult<SearchEntityResult[]>(
      CMDK_SPACES_ANYWHERE_QUERY,
      { query: ctx.query, limit: LIMIT },
    );
    if (isErr(result)) return result;
    const entities = Array.isArray(result.data) ? result.data : [];
    return ok(entities.map(entity => this.toItemFromEntity(entity)));
  }

  // ── Mapping ───────────────────────────────────────────────────────

  private toItemFromMap(space: SearchApiSpace, locationId: string): CmdkSpaceItem {
    const amenities = (space.amenities ?? []).map(a => a.name);
    return {
      id: space.id,
      kind: 'space',
      title: space.name,
      subtitle: join([space.floorName, this.capacityLabel(space.capacity)]),
      // The ranker's haystack is title + subtitle + aliases only, so without
      // this "whiteboard" or "boardroom" matches nothing even though the
      // backend found the space by exactly that word.
      aliases: [this.spaceTypeNoun(space.spaceType), space.email, ...amenities]
        .filter((v): v is string => !!v),
      spaceType: this.spaceTypeNoun(space.spaceType),
      floorName: space.floorName,
      locationId,
      capacity: space.capacity,
      availability: availabilityFrom(space.availableAtRequestedTime),
      amenities,
    };
  }

  private toItemFromEntity(entity: SearchEntityResult): CmdkSpaceItem {
    return {
      id: entity.id,
      kind: 'space',
      title: entity.name,
      subtitle: join([entity.locationName, entity.floorName]),
      aliases: [this.spaceTypeNoun(entity.subtype)].filter((v): v is string => !!v),
      spaceType: this.spaceTypeNoun(entity.subtype),
      floorName: entity.floorName,
      locationName: entity.locationName,
      // No locationId on this path: EntityResultType does not carry one, so a
      // row found from /home cannot deep-link to a focused map. The preview
      // offers the space detail panel, which only needs the id.
      availability: 'unknown',
    };
  }

  /**
   * Raw backend value → display noun. The raw strings ('meetingroom',
   * 'focusSpace', …) are not user-facing, and no exported label map existed:
   * the nearest were an unexported admin constant and a set of plural chip
   * labels. Unknown values fall back to the generic noun rather than leaking
   * a camelCase identifier into the UI.
   */
  private spaceTypeNoun(raw?: string): string {
    const key = `shell.cmdk.space_type.${raw ?? 'space'}`;
    const label = this.transloco.translate(key);
    // Transloco renders a missing key as the key itself.
    return label === key ? this.transloco.translate('shell.cmdk.space_type.space') : label;
  }

  private capacityLabel(capacity?: number): string | undefined {
    if (!capacity) return undefined;
    return this.transloco.translate(
      capacity === 1 ? 'shell.cmdk.capacity.one' : 'shell.cmdk.capacity.other',
      { count: capacity },
    );
  }
}

function availabilityFrom(available?: boolean): CmdkAvailability {
  if (available === true) return 'available';
  if (available === false) return 'busy';
  return 'unknown';
}

function join(parts: readonly (string | undefined)[]): string | undefined {
  const kept = parts.filter((p): p is string => !!p);
  return kept.length ? kept.join(' · ') : undefined;
}
